import { useMemo, useRef } from "react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table, TableRow } from "@tiptap/extension-table";
import { DisableDragDrop } from "components/TipTapEditorPlugins/DisableDragDrop";
import {
  AvatarMention,
  DocumentMention,
  createMentionSuggestionOptions
} from "components/TipTapEditorPlugins/MentionSuggestion";
import {
  QBRTableCell,
  QBRCellCallbacks,
  TeamCellCallbacks
} from "./QBRTableCellExtension";
import { QBRTableHeader } from "./QBRTableHeaderExtension";
import { useGlobal } from "@vojomo-core/shared";

interface UseQBREditorOptions {
  html: string | null;
  personalCallbacks?: QBRCellCallbacks;
  teamCallbacks?: TeamCellCallbacks;
}

export const useQBREditor = ({
  html,
  personalCallbacks,
  teamCallbacks
}: UseQBREditorOptions) => {
  const { view } = useGlobal();

  const personalRef = useRef(personalCallbacks);
  personalRef.current = personalCallbacks;

  const teamRef = useRef(teamCallbacks);
  teamRef.current = teamCallbacks;

  // Stable proxy objects that delegate to refs, so extensions never change
  const stablePersonal = useMemo<QBRCellCallbacks | undefined>(
    () =>
      personalCallbacks
        ? {
            getScore: (...args) => personalRef.current!.getScore(...args),
            getRationale: (...args) =>
              personalRef.current!.getRationale(...args),
            setScore: (...args) => personalRef.current!.setScore(...args),
            setRationale: (...args) =>
              personalRef.current!.setRationale(...args),
            getRowColor: (...args) => personalRef.current!.getRowColor(...args),
            getEditingRow: () => personalRef.current!.getEditingRow(),
            setEditingRow: (...args) =>
              personalRef.current!.setEditingRow(...args)
          }
        : undefined,
    [!!personalCallbacks]
  );

  const stableTeam = useMemo<TeamCellCallbacks | undefined>(
    () =>
      teamCallbacks
        ? {
            get owners() {
              return teamRef.current!.owners;
            },
            getRowColor: (...args) => teamRef.current!.getRowColor(...args),
            getScoreValue: (...args) => teamRef.current!.getScoreValue(...args),
            getScoreBadgeClass: (...args) =>
              teamRef.current!.getScoreBadgeClass(...args),
            getScoreBadgeColor: (...args) =>
              teamRef.current!.getScoreBadgeColor(...args),
            getVarianceColor: (...args) =>
              teamRef.current!.getVarianceColor(...args),
            getRowStats: (...args) => teamRef.current!.getRowStats(...args),
            getOwnerAverage: (...args) =>
              teamRef.current!.getOwnerAverage(...args),
            getObjectiveAvgAverage: () =>
              teamRef.current!.getObjectiveAvgAverage(),
            getObjectiveVarAverage: () =>
              teamRef.current!.getObjectiveVarAverage()
          }
        : undefined,
    [!!teamCallbacks, view]
  );

  const extensions = useMemo(
    () => [
      StarterKit.configure({ undoRedo: false }),
      Table,
      TableRow,
      QBRTableHeader,
      QBRTableCell.configure({
        personalCallbacks: stablePersonal,
        teamCallbacks: stableTeam
      } as any),
      DisableDragDrop,
      AvatarMention.configure({
        HTMLAttributes: { class: "mention" },
        suggestion: createMentionSuggestionOptions([], [])
      }),
      DocumentMention.configure({
        HTMLAttributes: { class: "mention-document" }
      })
    ],
    [stablePersonal, stableTeam]
  );

  const editor = useEditor(
    {
      extensions,
      editable: false,
      content: html || ""
    },
    [html, extensions]
  );

  return editor;
};
